import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeNavigation.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw, Plus } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTabSwitcher from '../components/MicrositeTabSwitcher/MicrositeTabSwitcher'
import MicrositeSortableNavList from '../components/MicrositeSortableNavList/MicrositeSortableNavList'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeVersionDrawer from '../components/MicrositeVersionDrawer/MicrositeVersionDrawer'

const NAVIGATION_TYPE_OPTIONS = [
    { value: 'FLOATING', label: 'Floating' },
    { value: 'FIXED', label: 'Fixed' }
]

const EMPTY_LINK = (sortOrder) => ({ label: '', path: '/', isVisible: true, sortOrder })

const MicrositeNavigation = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [activeTab, setActiveTab] = useState('top')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [historyOpen, setHistoryOpen] = useState(false)

    const [topLinks, setTopLinks] = useState([])
    const [bottomLinks, setBottomLinks] = useState([])
    const [navigationType, setNavigationType] = useState('FLOATING')

    const [originalTop, setOriginalTop] = useState([])
    const [originalBottom, setOriginalBottom] = useState([])
    const [originalNavType, setOriginalNavType] = useState('FLOATING')

    const isTopDirty = useMemo(() => JSON.stringify(topLinks) !== JSON.stringify(originalTop), [topLinks, originalTop])
    const isBottomDirty = useMemo(
        () => JSON.stringify(bottomLinks) !== JSON.stringify(originalBottom) || navigationType !== originalNavType,
        [bottomLinks, originalBottom, navigationType, originalNavType]
    )
    const isDirty = activeTab === 'top' ? isTopDirty : isBottomDirty

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadNav = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            const nav = res.response?.nav || {}
            const top = (nav.top || []).map((l, i) => ({ ...l, sortOrder: i }))
            const bottom = (nav.bottom?.links || []).map((l, i) => ({ ...l, sortOrder: i }))
            const navType = nav.bottom?.navigationType || 'FLOATING'

            setTopLinks(top)
            setOriginalTop(top)
            setBottomLinks(bottom)
            setOriginalBottom(bottom)
            setNavigationType(navType)
            setOriginalNavType(navType)
        } else {
            showToast('Could not load navigation. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadNav()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const handleAddLink = () => {
        if (activeTab === 'top') {
            setTopLinks((prev) => [...prev, EMPTY_LINK(prev.length)])
        } else {
            setBottomLinks((prev) => [...prev, EMPTY_LINK(prev.length)])
        }
    }

    const handleSave = async () => {
        setSaving(true)
        if (activeTab === 'top') {
            const res = await post('micrositeUpdateStoreNav', { navType: 'TOP', links: topLinks })
            setSaving(false)
            if (res?.status === 'success') {
                setOriginalTop(topLinks)
                showToast('Top navigation saved successfully')
            } else {
                showToast('Could not save top navigation', 'error')
            }
        } else {
            const res = await post('micrositeUpdateStoreBottomNav', { navigationType, links: bottomLinks })
            setSaving(false)
            if (res?.status === 'success') {
                setOriginalBottom(bottomLinks)
                setOriginalNavType(navigationType)
                showToast('Bottom navigation saved successfully')
            } else {
                showToast('Could not save bottom navigation', 'error')
            }
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading navigation…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Navigation"
                subtitle="The links shown in your top menu and mobile bottom bar."
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onHistory={() => setHistoryOpen(true)}
            />

            <MicrositeTabSwitcher
                value={activeTab}
                onChange={setActiveTab}
                tabs={[
                    { value: 'top', label: 'Top navigation' },
                    { value: 'bottom', label: 'Bottom navigation' }
                ]}
            />

            {activeTab === 'top' ? (
                <section className={styles.fieldGroup}>
                    <h2 className={styles.groupTitle}>Top navigation links</h2>
                    <p className={styles.groupDesc}>Shown across the top of every page on your microsite.</p>

                    <MicrositeSortableNavList links={topLinks} onChange={setTopLinks} />

                    <button type="button" className={styles.addBtn} onClick={handleAddLink}>
                        <Plus size={14} />
                        Add link
                    </button>
                </section>
            ) : (
                <section className={styles.fieldGroup}>
                    <h2 className={styles.groupTitle}>Bottom navigation links</h2>
                    <p className={styles.groupDesc}>Shown as a mobile bottom bar on small screens.</p>

                    <SelectInput
                        label="Navigation style"
                        value={navigationType}
                        onChange={setNavigationType}
                        options={NAVIGATION_TYPE_OPTIONS}
                    />

                    <MicrositeSortableNavList links={bottomLinks} onChange={setBottomLinks} />

                    <button type="button" className={styles.addBtn} onClick={handleAddLink}>
                        <Plus size={14} />
                        Add link
                    </button>
                </section>
            )}

            <MicrositeVersionDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                tabKey={activeTab === 'top' ? 'nav_top' : 'nav_bottom'}
                storeIdentifier={activeStore?.storeIdentifier}
                vendorIdentifier={activeStore?.vendorIdentifier}
                onRolledBack={loadNav}
            />
        </div>
    )
}

export default MicrositeNavigation